import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import {
  getCategoryLabel,
  getSystemModels,
} from '../../../data/systemModels';
import {
  FAVORITE_TYPE_SYSTEM,
  FAVORITE_TYPE_USER,
} from '../../../services/favoriteModelsService';
import { useFavoriteModels } from '../../../context/FavoriteModelsContext';
import LoadingMark from '../../../components/ui/LoadingMark';
import ModelLibraryCardShell from './ModelLibraryCardShell';
import ModelListItem from './ModelListItem';

const PREVIEW_LIMIT = 10;

const FavoritesCard = ({ userTemplates = [], templatesLoading = false }) => {
  const navigate = useNavigate();
  const { favorites, isLoading, isSaving, isFavorite, toggleFavorite } =
    useFavoriteModels();

  const systemById = useMemo(() => {
    const map = new Map();
    getSystemModels().forEach((model) => {
      map.set(model.id, model);
    });
    return map;
  }, []);

  const userById = useMemo(() => {
    const map = new Map();
    userTemplates.forEach((template) => {
      const id = String(template.template_id ?? template.id ?? '');
      if (id) map.set(id, template);
    });
    return map;
  }, [userTemplates]);

  const resolvedFavorites = useMemo(() => {
    return favorites
      .map((favorite) => {
        if (favorite.type === FAVORITE_TYPE_SYSTEM) {
          const model = systemById.get(favorite.id);
          if (!model) return null;
          return {
            key: `system:${favorite.id}`,
            type: FAVORITE_TYPE_SYSTEM,
            id: favorite.id,
            label: model.name,
            subtitle: getCategoryLabel(model.category),
          };
        }

        const template = userById.get(favorite.id);
        if (!template) {
          // Keep a soft entry while templates are loading; hide if confirmed missing.
          if (templatesLoading) {
            return {
              key: `user:${favorite.id}`,
              type: FAVORITE_TYPE_USER,
              id: favorite.id,
              label: 'Carregando modelo...',
              subtitle: 'Meus Modelos',
              pending: true,
            };
          }
          return null;
        }

        return {
          key: `user:${favorite.id}`,
          type: FAVORITE_TYPE_USER,
          id: favorite.id,
          label: template.name || 'Sem nome',
          subtitle: 'Meus Modelos',
        };
      })
      .filter(Boolean)
      .slice(0, PREVIEW_LIMIT);
  }, [favorites, systemById, userById, templatesLoading]);

  const handleOpen = (item) => {
    if (item.pending) return;
    if (item.type === FAVORITE_TYPE_SYSTEM) {
      navigate(
        `/contract-generator?document_id=${encodeURIComponent(String(item.id))}`
      );
      return;
    }
    navigate(
      `/contract-generator?user_template_id=${encodeURIComponent(String(item.id))}`
    );
  };

  const showLoading = isLoading || (templatesLoading && favorites.some((f) => f.type === FAVORITE_TYPE_USER));

  return (
    <ModelLibraryCardShell className="border-t-2 border-t-amber-500/40">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-amber-500/10 shrink-0">
          <Icon name="Star" size={18} className="text-amber-500" />
        </div>
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-foreground tracking-tight truncate">
            Meus Favoritos
          </h2>
          <p className="text-xs text-muted-foreground truncate">
            Modelos do sistema e personalizados
          </p>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto space-y-1 mb-1 pr-0.5">
        {showLoading ? (
          <div className="flex justify-center py-8">
            <LoadingMark
              size="xs"
              message="Carregando favoritos..."
              messageClassName="text-xs"
            />
          </div>
        ) : resolvedFavorites.length === 0 ? (
          <p className="text-xs text-muted-foreground py-6 text-center px-2">
            Favorite modelos com a estrela para vê-los aqui.
          </p>
        ) : (
          resolvedFavorites.map((item) => (
            <ModelListItem
              key={item.key}
              label={item.label}
              onClick={() => handleOpen(item)}
              isFavorite={isFavorite(item.type, item.id)}
              favoriteDisabled={isSaving || item.pending}
              onToggleFavorite={() => toggleFavorite(item.type, item.id)}
            />
          ))
        )}
      </div>
    </ModelLibraryCardShell>
  );
};

export default FavoritesCard;
